import { Polygon, MultiPolygon } from 'geojson';
import { Frame } from '@/types/location';
import { forwardRequestTo } from '@/api/forwarder';
import { optimisticallyUpdateLastViewed } from '@/api/location';
import { apiCall } from '@/utils/api';

export const getImagesForPolygon = async (
  id: string,
  geojson: Polygon | MultiPolygon,
  currentOrganization: string | null,
  signal?: AbortSignal,
  successCallback?: (id: string) => void,
  shouldUpdateLastViewed = true,
): Promise<
  | {
      frames: Frame[];
      cost: number;
      creditsRemaining: number;
      notEnoughCredits: boolean;
    }
  | { error: any }
> => {
  try {
    const organizationIdQuery = currentOrganization
      ? `?organizationId=${currentOrganization}`
      : '';

    const response = await forwardRequestTo({
      route: `developer/latest/poly2${organizationIdQuery}`,
      method: 'POST',
      data: geojson,
      signal,
    });

    if (!signal?.aborted) {
      if (shouldUpdateLastViewed) {
        optimisticallyUpdateLastViewed(id, currentOrganization);
      }
      successCallback && successCallback(id);
    }

    return response;
  } catch (error: any) {
    return { error };
  }
};

export interface MapFeatureFrame {
  url: string;
  idx: number | string;
  lon: number;
  lat: number;
  t: number;
  yaw?: number;
}
export type DetectionsByFrame = Record<
  number | string,
  { id: number; detectionInfo: [number, number, number, number] }[]
>;
export const getFramesForMapFeature = async (
  id: string,
): Promise<
  | {
      frames: MapFeatureFrame[];
      detectionsByFrame: DetectionsByFrame;
    }
  | { error: any }
> => {
  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}/developer/imagery/mf/${id}`,
      { method: 'POST', credentials: 'omit' },
    );

    return response;
  } catch (error: any) {
    return { error };
  }
};

export interface EventObservationFrame {
  timestamp: Date;
  sequence: string;
  idx: number;
  mount?: 'unknown' | 'front' | 'right' | 'left' | 'back';
  position: {
    lat: number;
    latRef: string;
    lon: number;
    lonRef: string;
    mountPosition?: string;
    gdop?: number;
    hdop?: number;
    pdop?: number;
    eph?: number;
    cno?: number;
  };
  width: number;
  height: number;
  imu: {
    acc_x?: number;
    acc_y?: number;
    acc_z?: number;
    gyro_x?: number;
    gyro_y?: number;
    gyro_z?: number;
  };
  url?: string;
}
export const getFramesForRoadwork = async (
  id: string,
): Promise<{ frames: EventObservationFrame[] } | { error: unknown }> => {
  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}/developer/imagery/eventObservation/${id}`,
      { method: 'GET', credentials: 'omit' },
    );

    return response;
  } catch (error: any) {
    return { error };
  }
};

export const createBursts = async (
  ids: string[],
  currentOrganization: string | null,
) => {
  const formattedIds = ids.map(id => ({ trackedLocation: id }));

  try {
    const organizationIdQuery = currentOrganization
      ? `?organizationId=${currentOrganization}`
      : '';

    const response = await forwardRequestTo({
      route: `developer/burst/create${organizationIdQuery}`,
      method: 'POST',
      data: formattedIds,
    });

    return response;
  } catch (error: any) {
    return { error };
  }
};
